import { api } from '../../../utils/client';
import type {
  AdminOrg,
  BulkActionResponse,
  BulkCreateResponse,
  OrgFormValue,
  OrgListResponse,
  OrgsMeta,
  PersonOption,
} from './types';
import type { BulkActionResponse as UserBulkActionResponse } from '../users/types';
import { formToPayload } from './types';

export async function fetchOrgsMeta() {
  return api<OrgsMeta>('/api/admin/organizations/meta/');
}

export interface OrgListParams {
  q?: string;
  tier?: number | '';
  currency?: number | '';
  is_active?: '' | 'true' | 'false';
  page?: number;
  page_size?: number;
}

export async function fetchOrgs(params: OrgListParams) {
  const search = new URLSearchParams();
  if (params.q) search.set('q', params.q);
  if (params.tier) search.set('tier', String(params.tier));
  if (params.currency) search.set('currency', String(params.currency));
  if (params.is_active) search.set('is_active', params.is_active);
  search.set('page', String(params.page || 1));
  search.set('page_size', String(params.page_size || 50));
  return api<OrgListResponse>(`/api/admin/organizations/?${search.toString()}`);
}

export async function createOrg(value: OrgFormValue) {
  return api<AdminOrg>('/api/admin/organizations/', {
    method: 'POST',
    body: JSON.stringify(formToPayload(value, 'create')),
  });
}

export async function updateOrg(id: number, value: OrgFormValue) {
  return api<AdminOrg>(`/api/admin/organizations/${id}/`, {
    method: 'PATCH',
    body: JSON.stringify(formToPayload(value, 'edit')),
  });
}

export async function setOrgActive(id: number, isActive: boolean) {
  return api<AdminOrg>(`/api/admin/organizations/${id}/set-active/`, {
    method: 'POST',
    body: JSON.stringify({ is_active: isActive }),
  });
}

export async function switchOrg(id: number) {
  return api<{ org_id: number; org_name: string }>(`/api/admin/organizations/${id}/switch/`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export async function searchPeople(q: string) {
  return api<PersonOption[]>(`/api/admin/organizations/search-people/?q=${encodeURIComponent(q)}`);
}

export async function downloadOrgTemplate() {
  const res = await fetch('/api/admin/organizations/bulk-template/', {
    credentials: 'include',
  });
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = await res.json();
      message = body?.detail || body?.error || message;
    } catch {
      /* ignore */
    }
    throw new Error(message || 'Failed to download template');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'organization_import_template.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export async function bulkCreateOrgs(csv: string, dryRun: boolean) {
  return api<BulkCreateResponse>('/api/admin/organizations/bulk-create/', {
    method: 'POST',
    body: JSON.stringify({ csv, dry_run: dryRun }),
  });
}

export async function bulkOrgAction(payload: Record<string, unknown>) {
  return api<BulkActionResponse>('/api/admin/organizations/bulk-action/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function bulkOrgMembershipAction(payload: {
  user_ids: number[];
  organization_id: number;
  action: 'give_membership' | 'remove_membership';
}) {
  return api<UserBulkActionResponse>(
    '/api/admin/organizations/memberships/',
    {
      method: 'POST',
      body: JSON.stringify(payload),
    }
  );
}
